import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { FAQS } from "../data/content";

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(0); // First expanded by default
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return FAQS;
    const q = searchQuery.toLowerCase();
    return FAQS.filter(
      (item) =>
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const toggleAccordion = (originalIdx) => {
    setOpenIndex(openIndex === originalIdx ? null : originalIdx);
  };

  return (
    <>
      <SEO
        title="Spørgsmål og svar (FAQ)"
        description="Få svar på ofte stillede spørgsmål om naturlig hovtrimning, ISNHCP, barfods-overgang, forfangenhed og kørsel i Jylland hos Hoofcare."
      />

      {/* Header Banner */}
      <section className="bg-forest-900 text-white py-14 lg:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
            Ofte stillede spørgsmål
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-sand-50">
            FAQ – Spørgsmål & Svar
          </h1>
          <p className="text-sand-200 max-w-2xl text-base leading-relaxed">
            Svar på alle 30 hyppige spørgsmål om naturlig hovtrimning, barfods filosofien, forfangenhed og hesteadfærd.
          </p>
        </div>
      </section>

      {/* Accordion Section */}
      <section className="py-16 bg-sand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Search bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-charcoal-400">
              <i className="fa-solid fa-magnifying-glass text-sm"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søg i spørgsmål og svar (f.eks. forfangenhed, sko, vandrefold)..."
              className="w-full pl-11 pr-4 py-3.5 bg-white border border-sand-300 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-forest-700 shadow-2xs transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-charcoal-400 hover:text-charcoal-700 cursor-pointer text-xs"
              >
                Nulstil
              </button>
            )}
          </div>

          {filteredFaqs.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-sand-300 space-y-3">
              <p className="text-charcoal-700 font-semibold text-base">
                Ingen spørgsmål matchede din søgning "{searchQuery}".
              </p>
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="px-4 py-2 bg-sand-200 text-forest-900 rounded-lg text-xs font-bold hover:bg-sand-300 transition-colors cursor-pointer"
              >
                Vis alle 30 spørgsmål
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq) => {
                // Find original index to ensure correct accordion toggle
                const originalIdx = FAQS.findIndex((f) => f.question === faq.question);
                const isOpen = openIndex === originalIdx;

                return (
                  <div
                    key={originalIdx}
                    className="bg-white rounded-2xl border border-sand-300 shadow-2xs overflow-hidden transition-all duration-200"
                  >
                    <button
                      onClick={() => toggleAccordion(originalIdx)}
                      type="button"
                      className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-700 hover:bg-sand-50/80 transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="font-bold text-forest-900 text-base sm:text-lg leading-snug flex items-start gap-3">
                        <span className="w-8 h-8 rounded-full bg-forest-100 text-forest-800 flex items-center justify-center text-xs shrink-0 font-bold mt-0.5">
                          {originalIdx + 1}
                        </span>
                        <span>{faq.question.replace(/^\d+\.\s*/, "")}</span>
                      </span>
                      <span className="w-8 h-8 rounded-full bg-sand-100 text-forest-900 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-300 ${isOpen ? "rotate-180 text-emerald-700" : ""}`}></i>
                      </span>
                    </button>

                    <div
                      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-5 pb-6 pt-2 sm:px-6 border-t border-sand-100">
                          <div className="text-charcoal-700 leading-relaxed text-sm sm:text-base pl-11 whitespace-pre-line space-y-3">
                            {faq.answer}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Prompt for further questions */}
          <div className="bg-sand-100 p-8 rounded-2xl border border-sand-300 text-center space-y-4">
            <h3 className="text-xl font-bold text-forest-900">
              Fandt du ikke svaret på dit spørgsmål?
            </h3>
            <p className="text-sm text-charcoal-700 max-w-md mx-auto leading-relaxed">
              Du er altid meget velkommen til at kontakte mig direkte pr. telefon eller e-mail med spørgsmål vedrørende din hest.
            </p>
            <div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-semibold text-sm transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-envelope text-emerald-400"></i>
                <span>Send en besked på kontaktsiden</span>
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
